import React, { useEffect, useRef, useState } from 'react';
import Globe from 'react-globe.gl';
import { motion, useInView } from 'framer-motion';

const operationCenters = [
  { name: 'Johannesburg', lat: -26.2041, lng: 28.0473, size: 1.5, type: 'National Command Centre' },
  { name: 'Cape Town', lat: -33.9249, lng: 18.4241, size: 1.0, type: 'Regional Hub' },
  { name: 'Durban', lat: -29.8587, lng: 31.0218, size: 1.0, type: 'Regional Hub' },
  { name: 'Pretoria', lat: -25.7479, lng: 28.2293, size: 0.8, type: 'Tactical Base' },
  { name: 'Gqeberha', lat: -33.9608, lng: 25.6022, size: 0.8, type: 'Tactical Base' },
  { name: 'Bloemfontein', lat: -29.1141, lng: 26.2208, size: 0.8, type: 'Tactical Base' },
  { name: 'Limpopo', lat: -23.9045, lng: 29.4688, size: 0.8, type: 'Tactical Base' },
];

const InteractiveMap = () => {
  const globeEl = useRef();
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-10%" });
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1000);
  const [countries, setCountries] = useState({ features: [] });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetch('https://raw.githubusercontent.com/vasturiano/react-globe.gl/master/example/datasets/ne_110m_admin_0_countries.geojson')
      .then(res => res.json())
      .then(setCountries);
  }, []);

  useEffect(() => {
    if (globeEl.current && isInView) {
      // Auto-rotate
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 0.5;
      globeEl.current.controls().enableZoom = true;
      globeEl.current.controls().minDistance = 150; // allow deep zoom
      
      // Set initial POV to focus on South Africa, zoomed in
      globeEl.current.pointOfView({ lat: -29, lng: 24, altitude: 0.15 }, 2000);
    }
  }, [isInView]);

  return (
    <section className="py-32 bg-[#050505] relative overflow-hidden" id="locations" ref={containerRef}>
      <div className="container mx-auto px-6 relative z-10">
        <div className="mb-16">
          <motion.h2 
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-[#FFD700] font-space-grotesk tracking-widest text-sm uppercase mb-4 flex items-center gap-4"
          >
            <span className="w-12 h-[1px] bg-[#FFD700]"></span>
            Global Footprint
          </motion.h2>
          <motion.h3 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-6xl font-space-grotesk font-bold uppercase tracking-tight max-w-2xl mb-6"
          >
            Operation <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-400 to-gray-600">Centers.</span>
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-lg md:text-xl font-light leading-relaxed max-w-3xl"
          >
            iMvula has a national footprint servicing clients across South Africa, with the following key locations:
          </motion.p>
        </div>

        <div className="relative w-full h-[600px] flex justify-center items-center rounded-2xl bg-[#0a0a0a] border border-white/5 overflow-hidden group">
          {/* Subtle glow behind the globe */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#FFD700]/10 rounded-full blur-[100px] pointer-events-none transition-opacity duration-1000 group-hover:opacity-50"></div>
          
          {isInView && (
            <Globe
              ref={globeEl}
              width={windowWidth < 768 ? windowWidth - 48 : windowWidth > 1200 ? 1152 : windowWidth - 96}
              height={600}
              backgroundColor="rgba(5, 5, 5, 1)"
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-water.png"
              bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
              
              // Polygons for distinct countries
              polygonsData={countries.features}
              polygonCapColor={() => '#111111'}
              polygonSideColor={() => '#000000'}
              polygonStrokeColor={() => '#333333'}
              
              // Customizing the points
              pointsData={operationCenters}
              pointLat="lat"
              pointLng="lng"
              pointColor={() => '#FFD700'}
              pointAltitude={d => d.size * 0.05}
              pointRadius={d => d.size * 0.5}
              pointsMerge={false}
              
              // Custom rings for pulse effect
              ringsData={operationCenters}
              ringLat="lat"
              ringLng="lng"
              ringColor={() => t => `rgba(255, 215, 0, ${1 - t})`}
              ringMaxRadius={d => d.size * 5}
              ringPropagationSpeed={2}
              ringRepeatPeriod={1000}
              
              // HTML labels for centers
              htmlElementsData={operationCenters}
              htmlElement={d => {
                const el = document.createElement('div');
                el.style.transform = 'translate(-50%, -100%)';
                el.style.pointerEvents = 'none';
                el.style.marginTop = '-15px';
                el.innerHTML = `
                  <div class="flex flex-col items-center drop-shadow-xl">
                    <div class="bg-black/90 backdrop-blur-md border border-[#FFD700]/40 px-3 py-1.5 rounded-sm whitespace-nowrap">
                      <p class="text-[#FFD700] font-space-grotesk text-xs font-bold uppercase">${d.name}</p>
                      <p class="text-gray-400 font-inter text-[10px]">${d.type}</p>
                    </div>
                    <div class="w-[1px] h-6 bg-gradient-to-b from-[#FFD700]/80 to-transparent"></div>
                  </div>
                `;
                return el;
              }}
              htmlAltitude={d => d.size * 0.05}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default InteractiveMap;
